import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }),
};

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const { t } = useLang();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error(t('common.fill_required'));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t('contact.success'));
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      toast.error(t('contact.fail'));
    }
    setSending(false);
  };

  return (
    <div className="pt-20 min-h-screen" data-testid="contact-page">
      <section className="py-20 lg:py-28 bg-[#121215] border-b border-[rgba(255,255,255,0.06)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="overline mb-4">{t('contact.overline')}</p>
          <h1 className="text-5xl lg:text-6xl font-light mb-6">
            {t('contact.h_1')} <span className="font-bold gold-gradient-text">{t('contact.h_2')}</span>
          </h1>
          <p className="text-[#A3A19C] text-lg max-w-xl">
            {t('contact.sub')}
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Form */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-[#A3A19C] mb-2">{t('contact.name')} *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      data-testid="contact-name"
                      className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                      placeholder={t('contact.ph_name')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A3A19C] mb-2">{t('common.email')} *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      data-testid="contact-email"
                      className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-[#A3A19C] mb-2">{t('common.phone')}</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      data-testid="contact-phone"
                      className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                      placeholder="+973 XXXX XXXX" dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A3A19C] mb-2">{t('contact.subject')} *</label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={e => setForm({ ...form, subject: e.target.value })}
                      data-testid="contact-subject"
                      className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                      placeholder={t('contact.ph_subject')}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#A3A19C] mb-2">{t('contact.message')} *</label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-message"
                    className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300 resize-none"
                    placeholder={t('contact.ph_message')}
                  />
                </div>
                <button type="submit" disabled={sending} className="btn-primary inline-flex items-center gap-2" data-testid="contact-submit">
                  {sending ? t('contact.sending') : t('contact.send')} <Send className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={2} className="lg:col-span-2 space-y-8">
              <div className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-8">
                <h3 className="text-xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-6">{t('contact.direct')}</h3>
                <div className="space-y-5">
                  <a href="tel:+97333216371" className="flex items-center gap-4 text-sm text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300" data-testid="contact-info-phone">
                    <div className="w-10 h-10 rounded-full bg-[#08080A] border border-[rgba(212,175,55,0.2)] flex items-center justify-center">
                      <Phone className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-[#A3A19C] mb-0.5">{t('common.phone')}</p>
                      <p className="text-[#F5F2EB] ltr-iso">+973 3321 6371</p>
                    </div>
                  </a>
                  <a href="mailto:Princess4Fitness@gmail.com" className="flex items-center gap-4 text-sm text-[#A3A19C] hover:text-[#F5F2EB] transition-colors duration-300" data-testid="contact-info-email">
                    <div className="w-10 h-10 rounded-full bg-[#08080A] border border-[rgba(212,175,55,0.2)] flex items-center justify-center">
                      <Mail className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-[#A3A19C] mb-0.5">{t('common.email')}</p>
                      <p className="text-[#F5F2EB] ltr-iso">Princess4Fitness@gmail.com</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="w-10 h-10 rounded-full bg-[#08080A] border border-[rgba(212,175,55,0.2)] flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-[#A3A19C] mb-0.5">{t('common.location')}</p>
                      <p className="text-[#F5F2EB]">{t('common.location_value')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <a
                href="https://api.whatsapp.com/send/?phone=97333216371"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-6 hover:border-[rgba(212,175,55,0.3)] transition-border-color duration-300"
                data-testid="contact-whatsapp"
              >
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#F5F2EB]">{t('contact.whatsapp')}</p>
                  <p className="text-xs text-[#A3A19C]">{t('contact.whatsapp_sub')}</p>
                </div>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
